import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, FileText, CheckCircle, ArrowRight, BookOpen, ChevronRight, Calculator, TrendingUp, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BudgetPrevisionnel = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Budget Prévisionnel Copropriété : Guide Complet 2025 | Élaboration et vote</title>
        <meta name="description" content="Guide complet sur le budget prévisionnel de copropriété. Comment l'établir, le voter en AG, quelles dépenses inclure. Modèles et conseils pratiques." />
        <meta name="keywords" content="budget prévisionnel copropriété, vote budget copropriété, dépenses copropriété, provisions charges, budget AG copropriété" />
        <link rel="canonical" href="https://copropriete.app/budget-previsionnel-copropriete" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Se connecter</Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">C'est gratuit</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">Budget Prévisionnel de Copropriété</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comment établir, présenter et voter le budget prévisionnel de votre copropriété. Guide pratique avec modèles.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <main className="prose prose-slate prose-lg max-w-none">
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Calculator className="w-8 h-8 text-purple-600" />
              Qu'est-ce que le budget prévisionnel ?
            </h2>
            <p>
              Le <strong>budget prévisionnel</strong> est le document financier qui estime l'ensemble des dépenses prévisibles de la copropriété pour l'exercice à venir. Il sert de base au calcul des provisions sur charges que chaque copropriétaire devra verser.
            </p>
            <p>
              Le budget prévisionnel est <strong>obligatoire</strong> et doit être voté chaque année en assemblée générale, avant le début de l'exercice qu'il concerne.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-xl my-8">
              <p className="text-purple-900 font-medium m-0">
                💡 <strong>Bon à savoir :</strong> Le budget prévisionnel ne comprend pas les dépenses de travaux exceptionnels, qui font l'objet de votes séparés en AG.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <PieChart className="w-8 h-8 text-purple-600" />
              Composition du budget prévisionnel
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Dépenses courantes à inclure</h3>
            <ul>
              <li><strong>Administration générale :</strong> Honoraires syndic, frais postaux, frais bancaires</li>
              <li><strong>Entretien courant :</strong> Ménage, espaces verts, petites réparations</li>
              <li><strong>Assurances :</strong> Multirisque immeuble, responsabilité civile</li>
              <li><strong>Fluides :</strong> Électricité parties communes, eau</li>
              <li><strong>Contrats d'entretien :</strong> Ascenseur, chaufferie, VMC, portail</li>
              <li><strong>Charges de personnel :</strong> Gardien, employés d'immeuble</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Exemple de répartition type</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 py-3 text-left">Poste de dépenses</th>
                    <th className="px-4 py-3 text-right">Montant €</th>
                    <th className="px-4 py-3 text-right">% du budget</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="px-4 py-3">Chauffage collectif</td><td className="px-4 py-3 text-right">15 000</td><td className="px-4 py-3 text-right">30%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Entretien ascenseur</td><td className="px-4 py-3 text-right">8 000</td><td className="px-4 py-3 text-right">16%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Honoraires syndic</td><td className="px-4 py-3 text-right">6 000</td><td className="px-4 py-3 text-right">12%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Électricité commune</td><td className="px-4 py-3 text-right">5 000</td><td className="px-4 py-3 text-right">10%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Nettoyage</td><td className="px-4 py-3 text-right">6 000</td><td className="px-4 py-3 text-right">12%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Assurance</td><td className="px-4 py-3 text-right">4 000</td><td className="px-4 py-3 text-right">8%</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Divers</td><td className="px-4 py-3 text-right">6 000</td><td className="px-4 py-3 text-right">12%</td></tr>
                  <tr className="font-bold"><td className="px-4 py-3">TOTAL</td><td className="px-4 py-3 text-right">50 000</td><td className="px-4 py-3 text-right">100%</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              Élaboration du budget
            </h2>

            <h3 className="text-xl font-bold text-slate-800">Étape 1 : Analyse des comptes passés</h3>
            <p>
              Examinez les dépenses réelles des 2-3 dernières années pour identifier les tendances et les postes récurrents.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Étape 2 : Anticipation des évolutions</h3>
            <p>
              Prenez en compte l'inflation, les augmentations de contrats, les changements de prestataires prévus.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Étape 3 : Consultation du conseil syndical</h3>
            <p>
              Le conseil syndical doit examiner le projet de budget avant sa présentation en AG et peut proposer des ajustements.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Étape 4 : Vote en assemblée générale</h3>
            <p>
              Le budget est voté à la <strong>majorité de l'article 24</strong> (majorité des voix des présents et représentés).
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-purple-600" />
              Appels de provisions
            </h2>
            <p>
              Une fois le budget voté, le syndic procède aux <strong>appels de provisions</strong>, généralement trimestriels :
            </p>
            <ul>
              <li>1er trimestre : 25% du budget, exigible le 1er janvier</li>
              <li>2e trimestre : 25% du budget, exigible le 1er avril</li>
              <li>3e trimestre : 25% du budget, exigible le 1er juillet</li>
              <li>4e trimestre : 25% du budget, exigible le 1er octobre</li>
            </ul>

            <p>
              Chaque copropriétaire paie sa quote-part calculée selon ses tantièmes.
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Régularisation des charges</h2>
            <p>
              En fin d'exercice, les dépenses réelles sont comparées au budget prévisionnel :
            </p>
            <ul>
              <li><strong>Dépenses inférieures au budget :</strong> Le trop-perçu est déduit des prochains appels de charges</li>
              <li><strong>Dépenses supérieures au budget :</strong> Un complément est appelé aux copropriétaires</li>
            </ul>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
              <p className="text-emerald-900 font-medium m-0">
                ✅ <strong>Avec copropriete.app</strong>, créez votre budget prévisionnel en quelques clics et suivez son exécution en temps réel.
              </p>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 my-8 border border-purple-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Simplifiez votre gestion budgétaire</h3>
            <p className="text-slate-700 mb-6">
              copropriete.app vous aide à établir, suivre et optimiser le budget de votre copropriété.
            </p>
            <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-purple-600 hover:bg-purple-700">
              Essayer gratuitement pendant 7 jours
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default BudgetPrevisionnel;
